import { useAsanaMCPProjectsForWorkspace } from "@/components/EntitySelector/integrations/AsanaMCPSelector/useAsanaMCPProjectsForWorkspace";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { AsanaMcpProjectEntity, Entity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { ChevronsUpDown, Folder, X } from "lucide-react";
import React, { useMemo, useState } from "react";

interface AsanaProjectControlProps extends ControlProps {
  data: AsanaMcpProjectEntity | string | undefined;
}

const AsanaProjectControlComponent: React.FC<AsanaProjectControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  uischema,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("asana");

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string"
      ? data
      : data
        ? getEntityId(data as unknown as Entity)
        : null;

  // Get the workspace GID from the form data
  const ctx = useJsonForms();
  const workspaceGid = useMemo(() => {
    // Check if the uischema has a watchField option that specifies the workspace field path
    const watchField = (uischema as any)?.options?.watchField || "workspace";
    const formData = ctx?.core?.data || {};
    const workspaceValue = formData[watchField];
    // Handle both string ID and entity object for workspace
    return typeof workspaceValue === "string"
      ? workspaceValue
      : workspaceValue
        ? getEntityId(workspaceValue as unknown as Entity)
        : "";
  }, [ctx, uischema]);

  // Create a dummy connection to avoid undefined errors in the hook
  const connectionOrDummy =
    integration ||
    ({
      id: "",
      integration_id: "",
    } as any);

  const { data: projectsData, isLoading } = useAsanaMCPProjectsForWorkspace({
    connection: connectionOrDummy,
    workspaceGid,
    options: {
      enabled: !!integration && !!workspaceGid,
    },
  });

  const projects: AsanaMcpProjectEntity[] = (projectsData ||
    []) as AsanaMcpProjectEntity[];

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      const aName = a.data?.name || "";
      const bName = b.data?.name || "";
      return aName.localeCompare(bName);
    });
  }, [projects]);

  const filterFn = (project: AsanaMcpProjectEntity) => {
    const name = project.data?.name || "";
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  };

  const renderProjectIcon = () => {
    return (
      <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
        <Folder className="w-4! h-4!" />
      </div>
    );
  };

  const renderFn = (project: AsanaMcpProjectEntity) => {
    const projectId = project.data?.id || "";
    const projectName = project.data?.name || "";

    return (
      <CommandItem
        key={projectId}
        value={projectId}
        onSelect={() => {
          // Return full entity object
          handleChange(path, project);
          setOpen(false);
        }}
      >
        {renderProjectIcon()}
        <span className="truncate">{projectName}</span>
      </CommandItem>
    );
  };

  const selectedProject = useMemo(
    () => projects.find((project) => selectedId === project.data?.id),
    [projects, selectedId],
  );

  // Disable if no workspace is selected or no integration
  const isDisabled = !enabled || !integration || !workspaceGid;

  return (
    <div>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={isDisabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedProject ? (
              <div className="flex items-center gap-2 overflow-hidden">
                {renderProjectIcon()}
                <span className="truncate">{selectedProject.data?.name}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                {!integration
                  ? "Connect Asana first..."
                  : workspaceGid
                    ? "Select project..."
                    : "Select a workspace first..."}
              </span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedProject && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search projects..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>
                {isLoading ? (
                  <Spinner size="md" />
                ) : !integration ? (
                  "Connect Asana first."
                ) : workspaceGid ? (
                  "No projects found."
                ) : (
                  "Select a workspace first."
                )}
              </CommandEmpty>
              <CommandGroup>
                {sortedProjects.filter(filterFn).map(renderFn)}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const asanaProjectTester = rankWith(
  5,
  optionIs("control", "asana_project"),
);
export const AsanaProjectControl = withJsonFormsControlProps(
  AsanaProjectControlComponent,
);
